import React, { useEffect, useState } from "react";
import {
  getMainImageUrl,
  normalizeGallery,
} from "../../utils/fleetCardMedia";

const API_BASE_URL = "";

const emptyCard = {
  title: "",
  desc: "",
  capacity: "",
  dimensions: "",
  cabins: "",
  washroom: "",
  goodFor: "",
  imgUrl: "",
  gallery: [],
  inMumbai: true,
  inGoa: false,
  inLuxury: true,
  inExplore: false,
  petFriendly: false,
};

const cloneCard = (card) => {
  const gallery = normalizeGallery(card?.gallery, card?.imgUrl);

  return {
    ...emptyCard,
    ...card,
    _id: card?._id,
    title: card?.title || card?.category || "",
    gallery,
    imgUrl: getMainImageUrl({ gallery, imgUrl: card?.imgUrl }),
    inMumbai: card?.inMumbai === true,
    inGoa: card?.inGoa === true,
    inLuxury: card?.inLuxury === true,
    inExplore: card?.inExplore === true,
    petFriendly: card?.petFriendly === true,
  };
};

const getMediaUrl = (path) => {
  if (!path) {
    return "";
  }

  if (path.startsWith("http") || path.startsWith("/uploads/")) {
    return path;
  }

  return path;
};

const LocationFleetCms = ({
  pageKey,
  heading,
  intro,
  entityLabel = "Yacht",
  locationALabel = "Mumbai",
  locationBLabel = "Goa",
  fleetALabel = "Luxury Fleet",
  fleetBLabel = "Explore Fleet",
}) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(cloneCard(emptyCard));
  const [editorIndex, setEditorIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem("indiaboatsAdminToken");
  const isEditing = editorIndex !== null;
  const items = data?.fleetCards || [];

  const resetForm = () => {
    setEditorIndex(null);
    setForm(cloneCard(emptyCard));
  };

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/experiences/${encodeURIComponent(pageKey)}`
        );

        if (!response.ok) {
          throw new Error("Failed to load yachts");
        }

        const result = await response.json();
        setData(result);
        resetForm();
      } catch (error) {
        console.error("Fleet CMS fetch error:", error);
        alert("Failed to load yachts");
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [pageKey]);

  const handleFormChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleImageUpload = async (file) => {
    if (!file) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Image upload failed");
      }

      setForm((previous) => {
        const gallery = [...(previous.gallery || [])];
        const isFirst = gallery.length === 0;

        gallery.push({
          url: result.fileUrl,
          isMain: isFirst,
        });

        return {
          ...previous,
          gallery,
          imgUrl: isFirst ? result.fileUrl : previous.imgUrl || result.fileUrl,
        };
      });
    } catch (error) {
      console.error("Image upload error:", error);
      alert(error.message || "Failed to upload image");
    }
  };

  const setMainGalleryImage = (index) => {
    setForm((previous) => {
      const gallery = (previous.gallery || []).map((item, itemIndex) => ({
        ...item,
        isMain: itemIndex === index,
      }));
      const main = gallery[index];

      return {
        ...previous,
        gallery,
        imgUrl: main?.url || previous.imgUrl,
      };
    });
  };

  const removeGalleryImage = (index) => {
    setForm((previous) => {
      const gallery = (previous.gallery || []).filter(
        (_, itemIndex) => itemIndex !== index
      );
      const normalized = normalizeGallery(gallery);

      return {
        ...previous,
        gallery: normalized,
        imgUrl: getMainImageUrl({ gallery: normalized }),
      };
    });
  };

  const persist = async (fleetCards, successMessage) => {
    const response = await fetch(
      `${API_BASE_URL}/api/experiences/${encodeURIComponent(pageKey)}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          ...data,
          fleetCards,
        }),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || "Failed to save");
    }

    setData(result.data);
    alert(successMessage);
    return result.data;
  };

  const handleSave = async () => {
    if (!data) {
      return;
    }

    if (!String(form.title || "").trim()) {
      alert("Title is required");
      return;
    }

    if (!form.inMumbai && !form.inGoa) {
      alert(`Select ${locationALabel}, ${locationBLabel}, or both`);
      return;
    }

    if (!form.inLuxury && !form.inExplore) {
      alert(`Select ${fleetALabel}, ${fleetBLabel}, or both`);
      return;
    }

    try {
      setSaving(true);

      const nextItem = cloneCard(form);
      const nextItems = [...items];

      if (isEditing) {
        nextItems[editorIndex] = nextItem;
      } else {
        nextItems.push(nextItem);
      }

      await persist(
        nextItems,
        isEditing ? `${entityLabel} updated successfully` : `${entityLabel} saved successfully`
      );
      resetForm();
    } catch (error) {
      console.error("Fleet save error:", error);
      alert(error.message || "Failed to save yacht");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (index) => {
    setEditorIndex(index);
    setForm(cloneCard(items[index]));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (index) => {
    const item = items[index];
    const label = item?.title || `${entityLabel} ${index + 1}`;

    if (
      !window.confirm(
        `Delete "${label}"? This will remove it from the website.`
      )
    ) {
      return;
    }

    try {
      setSaving(true);

      const nextItems = items.filter((_, itemIndex) => itemIndex !== index);
      await persist(nextItems, "Yacht deleted successfully");

      if (editorIndex === index) {
        resetForm();
      } else if (editorIndex !== null && editorIndex > index) {
        setEditorIndex(editorIndex - 1);
      }
    } catch (error) {
      console.error("Fleet delete error:", error);
      alert(error.message || "Failed to delete yacht");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="cms-page">
        <div className="cms-page-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="cms-page">
      <div className="cms-page-header">
        <div>
          <h1>{heading}</h1>
          <p>{intro}</p>
        </div>

        <button
          type="button"
          className="cms-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : isEditing ? "Save Changes" : `Save ${entityLabel}`}
        </button>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>{isEditing ? `Update ${entityLabel}` : `${entityLabel} Details`}</h2>
          <p>
            {isEditing
              ? "Edit the fields below and save changes."
              : `Fill this form and save. One ${entityLabel.toLowerCase()} can be shown in ${locationALabel}, ${locationBLabel}, or both.`}
          </p>
        </div>

        <div className="cms-form-grid cms-form-grid-4">
          <div className="cms-form-group cms-form-compact">
            <label>Locations</label>
            <div className="cms-checkbox-row cms-checkbox-compact">
              <label className="cms-checkbox-option">
                <input
                  type="checkbox"
                  className="cms-checkbox-input"
                  checked={form.inMumbai === true}
                  onChange={(e) =>
                    handleFormChange("inMumbai", e.target.checked)
                  }
                />
                <span>{locationALabel}</span>
              </label>
              <label className="cms-checkbox-option">
                <input
                  type="checkbox"
                  className="cms-checkbox-input"
                  checked={form.inGoa === true}
                  onChange={(e) => handleFormChange("inGoa", e.target.checked)}
                />
                <span>{locationBLabel}</span>
              </label>
            </div>
          </div>

          <div className="cms-form-group cms-form-compact">
            <label>Show In</label>
            <div className="cms-checkbox-row cms-checkbox-compact">
              <label className="cms-checkbox-option">
                <input
                  type="checkbox"
                  className="cms-checkbox-input"
                  checked={form.inLuxury === true}
                  onChange={(e) =>
                    handleFormChange("inLuxury", e.target.checked)
                  }
                />
                <span>{fleetALabel}</span>
              </label>
              <label className="cms-checkbox-option">
                <input
                  type="checkbox"
                  className="cms-checkbox-input"
                  checked={form.inExplore === true}
                  onChange={(e) =>
                    handleFormChange("inExplore", e.target.checked)
                  }
                />
                <span>{fleetBLabel}</span>
              </label>
            </div>
          </div>

          <div className="cms-form-group cms-form-compact">
            <label>Pet Friendly</label>
            <div className="cms-checkbox-row cms-checkbox-compact">
              <label className="cms-checkbox-option">
                <input
                  type="checkbox"
                  className="cms-checkbox-input"
                  checked={form.petFriendly === true}
                  onChange={(e) =>
                    handleFormChange("petFriendly", e.target.checked)
                  }
                />
                <span>Show pet friendly</span>
              </label>
            </div>
          </div>

          <div className="cms-form-group cms-form-span-2">
            <label>Boat Images</label>
            <p className="cms-inquiries-empty" style={{ margin: "0 0 12px" }}>
              Upload multiple photos. Mark one as main — it appears on the boat
              card and as the first photo in yacht details.
            </p>
            <div className="cms-gallery-grid">
              {(form.gallery || []).map((image, index) => (
                <div
                  key={`${image.url}-${index}`}
                  className={`cms-gallery-item${
                    image.isMain ? " is-main" : ""
                  }`}
                >
                  <img src={getMediaUrl(image.url)} alt={`Boat ${index + 1}`} />
                  {image.isMain ? (
                    <span className="cms-gallery-badge">Main image</span>
                  ) : null}
                  <div className="cms-gallery-actions">
                    {!image.isMain ? (
                      <button
                        type="button"
                        className="cms-upload-btn"
                        onClick={() => setMainGalleryImage(index)}
                      >
                        Set as main
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="cms-delete-btn"
                      onClick={() => removeGalleryImage(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="cms-upload-actions" style={{ marginTop: 12 }}>
              <input
                type="file"
                id={`${pageKey}-image-editor`}
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="cms-file-input"
                onChange={(e) => {
                  handleImageUpload(e.target.files[0]);
                  e.target.value = "";
                }}
              />
              <label
                htmlFor={`${pageKey}-image-editor`}
                className="cms-upload-btn"
              >
                Add Image
              </label>
            </div>
          </div>

          <div className="cms-form-group">
            <label>Title</label>
            <input
              type="text"
              value={form.title || ""}
              onChange={(e) => handleFormChange("title", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Guests (total people on board)</label>
            <input
              type="text"
              value={form.capacity || ""}
              onChange={(e) => handleFormChange("capacity", e.target.value)}
              placeholder="e.g. 12 Guests"
            />
          </div>

          <div className="cms-form-group">
            <label>Size</label>
            <input
              type="text"
              value={form.dimensions || ""}
              onChange={(e) => handleFormChange("dimensions", e.target.value)}
              placeholder="e.g. 55 ft"
            />
          </div>

          <div className="cms-form-group">
            <label>Layout (cabin / washroom)</label>
            <div className="cms-twin-inputs">
              <input
                type="text"
                value={form.cabins || ""}
                onChange={(e) => handleFormChange("cabins", e.target.value)}
                placeholder="Cabin"
              />
              <input
                type="text"
                value={form.washroom || ""}
                onChange={(e) => handleFormChange("washroom", e.target.value)}
                placeholder="Washroom"
              />
            </div>
          </div>

          <div className="cms-form-group">
            <label>Good For</label>
            <input
              type="text"
              value={form.goodFor || ""}
              onChange={(e) => handleFormChange("goodFor", e.target.value)}
            />
          </div>

          <div className="cms-form-group cms-form-span-2">
            <label>Description</label>
            <textarea
              rows="3"
              value={form.desc || ""}
              onChange={(e) => handleFormChange("desc", e.target.value)}
            />
          </div>
        </div>

        <div className="cms-bottom-actions">
          <button
            type="button"
            className="cms-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : isEditing ? "Save Changes" : `Save ${entityLabel}`}
          </button>
          {isEditing ? (
            <button
              type="button"
              className="cms-upload-btn"
              onClick={resetForm}
              disabled={saving}
            >
              Cancel Update
            </button>
          ) : null}
        </div>
      </div>

      <div className="cms-section-card">
        <div className="cms-section-card-header">
          <h2>{entityLabel}s on this page</h2>
          <p>Update loads the form above. Delete asks for confirmation first.</p>
        </div>

        {items.length === 0 ? (
          <p className="cms-inquiries-empty">No yachts added yet.</p>
        ) : (
          <div className="cms-item-list">
            {items.map((card, index) => (
              <div
                className={`cms-item-row${
                  editorIndex === index ? " is-active" : ""
                }`}
                key={card._id || index}
              >
                <div className="cms-item-row-thumb">
                  {getMainImageUrl(card) ? (
                    <img
                      src={getMediaUrl(getMainImageUrl(card))}
                      alt={card.title || "Yacht"}
                    />
                  ) : (
                    <span>No image</span>
                  )}
                </div>
                <div className="cms-item-row-info">
                  <strong>{card.title || `${entityLabel} ${index + 1}`}</strong>
                  <span>
                    {[
                      card.inMumbai ? locationALabel : "",
                      card.inGoa ? locationBLabel : "",
                      card.inLuxury ? fleetALabel : "",
                      card.inExplore ? fleetBLabel : "",
                      card.petFriendly ? "Pet friendly" : "",
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  </span>
                </div>
                <div className="cms-item-row-actions">
                  <button
                    type="button"
                    className="cms-upload-btn"
                    onClick={() => startEdit(index)}
                  >
                    Update
                  </button>
                  <button
                    type="button"
                    className="cms-delete-btn"
                    onClick={() => handleDelete(index)}
                    disabled={saving}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationFleetCms;
